import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useLang } from "@/i18n/lang";

import { CONSENT_KEY, CONSENT_EVENT, readConsent, type CookieConsentState } from "@/lib/cookieConsent";

const applyConsent = (c: CookieConsentState) => {
  window.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: c }));
};
const save = (c: CookieConsentState) => {
  try { localStorage.setItem(CONSENT_KEY, JSON.stringify(c)); } catch { /* Fail closed when storage is unavailable. */ }
  applyConsent(readConsent());
};

const CookieConsent = () => {
  const [open, setOpen] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const lang = useLang();
  const useEnglishCopy = lang !== "he";
  const privacyPath = "/privacy";

  useEffect(() => {
    try {
      const raw = localStorage.getItem(CONSENT_KEY);
      if (!raw) {
        setOpen(true);
      } else {
        const consent = readConsent();
        setAnalytics(consent.analytics); setMarketing(consent.marketing);
        applyConsent(consent);
      }
    } catch {
      setOpen(true);
    }
  }, []);

  useEffect(() => {
    const reopen = () => { const c = readConsent(); setAnalytics(c.analytics); setMarketing(c.marketing); setShowPrefs(true); setOpen(true); };
    window.addEventListener("ofix:cookie-preferences", reopen);
    return () => window.removeEventListener("ofix:cookie-preferences", reopen);
  }, []);

  if (!open) return null;


  const acceptAll = () => {
    save({ necessary: true, analytics: true, marketing: true, ts: Date.now() });
    setOpen(false);
  };
  const rejectAll = () => {
    save({ necessary: true, analytics: false, marketing: false, ts: Date.now() });
    setOpen(false);
  };
  const savePrefs = () => {
    save({ necessary: true, analytics, marketing, ts: Date.now() });
    setOpen(false);
  };

  return (
    <div
      dir={useEnglishCopy ? "ltr" : "rtl"}
      role="dialog"
      aria-label={lang === "ru" ? "Настройки файлов cookie" : useEnglishCopy ? "Cookie settings" : "הגדרות עוגיות"}
      className="fixed inset-x-0 bottom-0 z-[60] p-2 sm:p-3"
    >
      <div className="mx-auto max-w-2xl bg-card border border-border rounded-2xl shadow-2xl p-3 sm:p-4">
        {!showPrefs ? (
          <>
            <h2 className="font-bold text-sm sm:text-base mb-1.5">
              {lang === "ru" ? "Мы используем файлы cookie" : useEnglishCopy ? "We use cookies" : "אנחנו משתמשים בעוגיות (Cookies)"}
            </h2>
            <p className="text-xs sm:text-sm text-muted-foreground mb-3">
              {useEnglishCopy ? (
                <>
                  {lang === "ru" ? "Необходимые файлы cookie обеспечивают работу сайта. Аналитика и маркетинг включаются с вашего согласия. Подробнее: " : "Essential cookies keep the site running. Analytics and marketing cookies are used only with your consent. Read more in the "}
                   <Link to={privacyPath} className="text-accent font-semibold hover:underline mx-1">{lang === "ru" ? "политика конфиденциальности (на иврите)" : "privacy policy (Hebrew)"}</Link>.
                </>
              ) : (
                <>
                  אנו משתמשים בעוגיות הכרחיות להפעלת האתר, ובעוגיות סטטיסטיקה ושיווק בכפוף להסכמתך —
                  בהתאם לחוק הגנת הפרטיות. תוכל/י לקרוא עוד ב
                  <Link to="/privacy" className="text-accent font-semibold hover:underline mx-1">מדיניות הפרטיות</Link>.
                </>
              )}
            </p>
            <div className="flex flex-col sm:flex-row gap-2">
              <Button variant="ghost" onClick={acceptAll} className="flex-1 bg-accent text-accent-foreground py-2 rounded-lg font-bold min-h-[48px] text-sm">
                 {lang === "ru" ? "Принять все" : useEnglishCopy ? "Accept all" : "אשר הכל"}
              </Button>
              <Button variant="ghost" onClick={rejectAll} className="flex-1 bg-muted text-foreground py-2 rounded-lg font-semibold min-h-[48px] text-sm">
                 {lang === "ru" ? "Отклонить необязательные" : useEnglishCopy ? "Reject non-essential" : "דחה לא-הכרחיות"}
              </Button>
              <Button variant="ghost" onClick={() => setShowPrefs(true)} className="flex-1 border border-border py-2 rounded-lg font-semibold min-h-[48px] text-sm">
                 {lang === "ru" ? "Настроить" : useEnglishCopy ? "Manage preferences" : "ניהול העדפות"}
              </Button>
            </div>
          </>
        ) : (
          <>
            <h2 className="font-bold text-sm sm:text-base mb-2">
               {lang === "ru" ? "Настройки файлов cookie" : useEnglishCopy ? "Cookie preferences" : "ניהול העדפות עוגיות"}
            </h2>
            <div className="space-y-2 mb-3">
              <label className="flex items-start gap-3 p-2.5 rounded-xl bg-muted/50">
                <input type="checkbox" checked disabled className="mt-1" />
                <div>
                   <div className="font-semibold text-xs sm:text-sm">{lang === "ru" ? "Необходимые (всегда включены)" : useEnglishCopy ? "Essential (always on)" : "הכרחיות (תמיד פעילות)"}</div>
                   <div className="text-[11px] sm:text-xs text-muted-foreground">{lang === "ru" ? "Нужны для работы сайта, безопасности и форм связи." : useEnglishCopy ? "Required for the site, security and contact forms." : "נדרשות להפעלת האתר, אבטחה וטפסי יצירת קשר."}</div>
                </div>
              </label>
              <label className="flex items-start gap-3 p-2.5 rounded-xl bg-muted/30 cursor-pointer">
                <input type="checkbox" checked={analytics} onChange={(e) => setAnalytics(e.target.checked)} className="mt-1" />
                <div>
                   <div className="font-semibold text-xs sm:text-sm">{lang === "ru" ? "Аналитика" : useEnglishCopy ? "Analytics" : "סטטיסטיקה וניתוח"}</div>
                   <div className="text-[11px] sm:text-xs text-muted-foreground">{lang === "ru" ? "GA4, PostHog — помогают улучшать сайт." : useEnglishCopy ? "GA4, PostHog — help me improve the site." : "GA4, PostHog — עוזרות לנו לשפר את האתר."}</div>
                </div>
              </label>
              <label className="flex items-start gap-3 p-2.5 rounded-xl bg-muted/30 cursor-pointer">
                <input type="checkbox" checked={marketing} onChange={(e) => setMarketing(e.target.checked)} className="mt-1" />
                <div>
                   <div className="font-semibold text-xs sm:text-sm">{lang === "ru" ? "Маркетинг" : useEnglishCopy ? "Marketing" : "שיווק ופרסום"}</div>
                   <div className="text-[11px] sm:text-xs text-muted-foreground">{lang === "ru" ? "Meta Pixel — оценка рекламных кампаний." : useEnglishCopy ? "Meta Pixel, remarketing — campaign measurement." : "Meta Pixel, רימרקטינג — מדידת קמפיינים והתאמת מודעות."}</div>
                </div>
              </label>
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <Button variant="ghost" onClick={savePrefs} className="flex-1 bg-accent text-accent-foreground py-2 rounded-lg font-bold min-h-[48px] text-sm">
                 {lang === "ru" ? "Сохранить настройки" : useEnglishCopy ? "Save preferences" : "שמור העדפות"}
              </Button>
              <Button variant="ghost" onClick={() => setShowPrefs(false)} className="flex-1 border border-border py-2 rounded-lg font-semibold min-h-[48px] text-sm">
                 {lang === "ru" ? "Назад" : useEnglishCopy ? "Back" : "חזרה"}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CookieConsent;
